import classNames from 'classnames'
import {gsap} from 'gsap'
import {ScrollTrigger} from 'gsap/ScrollTrigger'
import * as React from 'react'
import {render} from 'storyblok-rich-text-react-renderer'

import {makeEditable, unwrapAllNodeResolvers} from '@/common/utils'
import Buttons from '@/components/Buttons'
import Content from '@/components/Content'
import {Grid} from '@/components/Grid'
import Heading from '@/components/Heading'
import Image from '@/components/Image'
import Images from '@/components/Images'
import Testimonial from '@/components/Testimonial'

const FeaturesSlider = ({backgroundColors, heading, items = [], _editable}) => {
  const sectionRef = React.useRef()
  const coversRef = React.useRef([])
  const headingsRef = React.useRef([])
  const testimonialsRef = React.useRef([])
  const imagesRef = React.useRef([])
  const contentsRef = React.useRef([])
  const containerRef = React.useRef()

  React.useEffect(() => {
    const section = sectionRef.current
    const covers = coversRef.current
    const headings = headingsRef.current
    const testimonials = testimonialsRef.current
    const images = imagesRef.current
    const contents = contentsRef.current

    if (section) {
      const context = gsap.context(() => {
        const tl = gsap.timeline()

        ScrollTrigger.create({
          animation: tl,
          trigger: section,
          pin: true,
          pinType: 'transform',
          scrub: true,
          start: 'top-=30 top',
        })

        const initStepKey = 'init'
        const getStepKey = (index, delay) => `step-${index}` + delay
        const getSetKey = (index) => `${getStepKey(index)}+=0.15`

        covers.forEach((cover, index, covers) => {
          if (index > 0) {
            tl.set(cover, {opacity: 0.3, x: 0, y: index * -20}, initStepKey)
            tl.to(cover, {opacity: 1, x: 0, y: 0}, getStepKey(index))
            tl.to(covers[index - 1], {opacity: 0, x: 0, y: 80, duration: 0.25}, getStepKey(index))
          }
        })

        headings.forEach((heading, index, headings) => {
          if (index > 0) {
            tl.set(heading, {opacity: 0.3, x: 0, y: index * -20}, initStepKey)
            tl.to(heading, {opacity: 1, x: 0, y: 0}, getStepKey(index))
            tl.to(
              headings[index - 1],
              {opacity: 0, x: 0, y: '+=120px', duration: 0.25},
              getStepKey(index)
            )
          }
        })

        testimonials.forEach((testimonial, index, testimonials) => {
          if (index > 0) {
            tl.set(testimonial, {autoAlpha: 0}, initStepKey)
            tl.set(testimonial, {autoAlpha: 1}, getSetKey(index))
            tl.set(testimonials[index - 1], {autoAlpha: 0}, getSetKey(index))
          }
        })

        images.forEach((image, index, images) => {
          if (index > 0) {
            tl.set(image, {autoAlpha: 0}, initStepKey)
            tl.set(image, {autoAlpha: 1, duration: 0}, getSetKey(index))
            tl.set(images[index - 1], {autoAlpha: 0}, getSetKey(index))
          }
        })

        contents.forEach((content, index, contents) => {
          if (index > 0) {
            tl.set(content, {autoAlpha: 0}, initStepKey)
            tl.set(content, {autoAlpha: 1}, getSetKey(index))
            tl.set(contents[index - 1], {autoAlpha: 0}, getSetKey(index))
          }
        })
      })

      return () => context.revert()
    }
  }, [])

  React.useEffect(() => {
    const container = containerRef.current
    if (container) {
      const context = gsap.context(() => {
        gsap.fromTo(
          'path',
          {drawSVG: '0%'},
          {
            drawSVG: '100%',
            scrollTrigger: {
              trigger: container,
              start: 'center bottom',
            },
          }
        )
      }, container)

      return () => context.revert()
    }
  }, [])

  return (
    <section
      {...makeEditable(_editable)}
      className={classNames('overflow-hidden pt-16 md:pt-24', backgroundColors.current)}
      ref={containerRef}
    >
      <Grid>
        <Heading
          heading={heading}
          className='col-span-full text-center md:col-span-4 md:col-start-2 lg:col-span-6 lg:col-start-4'
        />
        <div className='col-span-full lg:col-span-7 lg:col-start-3'>
          <ArrowDown aria-hidden className='mx-auto md:mx-0 md:-mt-12' />
        </div>
        <div
          ref={sectionRef}
          className='col-span-full grid bg-[radial-gradient(47%_26%_at_51%_100%,rgba(227,243,32,0.4)_0%,rgba(227,243,32,0)_100%)]'
        >
          {items.map(
            (
              {_uid, headingTag, heading, content, cardImage, images, buttons, testimonial},
              index
            ) => {
              const HeadingTag = headingTag || 'p'
              return (
                <div
                  key={_uid}
                  className='col-start-1 row-start-1 flex min-h-screen flex-col justify-end h-1200:h-[1200px] h-1200:min-h-[unset]'
                >
                  <Grid className='h-full flex-shrink flex-grow !px-0 pt-20 pb-10'>
                    <div
                      className='relative col-span-full mb-10 lg:col-span-10 lg:col-start-2 lg:mb-0'
                      style={{zIndex: items.length - index}}
                    >
                      <Image
                        ref={(ref) => (coversRef.current[index] = ref)}
                        {...cardImage}
                        className='absolute inset-0 h-full w-full rounded-[theme(spacing.10)] object-cover'
                        sizes={{sm: 100}}
                      />

                      <HeadingTag
                        ref={(ref) => (headingsRef.current[index] = ref)}
                        className='heading-m-millik absolute inset-0 h-full w-full px-10 pt-16 text-center text-yellow-400 md:pt-8 lg:flex lg:items-center lg:justify-center lg:pt-0'
                      >
                        {render(heading, {nodeResolvers: unwrapAllNodeResolvers})}
                      </HeadingTag>

                      <div
                        ref={(ref) => (imagesRef.current[index] = ref)}
                        className='absolute -bottom-4 left-[10%] z-10 w-[80%] md:bottom-[10%] md:left-0 md:w-1/2 lg:bottom-[unset] lg:top-2/3 lg:w-[40%]'
                      >
                        <Images
                          images={images}
                          className='aspect-w-15 aspect-h-10 translate-y-10 md:-translate-y-2 lg:translate-y-0'
                          imageProps={{ratio: '15:10', fitIn: true, sizes: {md: 66, lg: 50}}}
                        />
                      </div>
                    </div>
                    <div
                      ref={(ref) => (testimonialsRef.current[index] = ref)}
                      className='absolute z-10 hidden w-fit md:col-span-4 md:col-start-3 md:block md:translate-y-62 lg:right-0 lg:col-span-4 lg:col-start-9 lg:col-end-12 lg:w-[33%] lg:translate-y-0'
                    >
                      <Testimonial {...testimonial.content} />
                    </div>
                  </Grid>

                  <Grid
                    ref={(ref) => (contentsRef.current[index] = ref)}
                    className='flex-none !px-0 pb-4 md:pb-10'
                  >
                    <Content
                      content={content}
                      size='s'
                      className='col-span-full text-center md:col-span-4 md:col-start-2 lg:col-span-3 lg:col-start-9 lg:text-left'
                    />

                    <Buttons
                      buttons={buttons}
                      className='z-10 col-span-full mt-10 flex justify-center lg:mt-18'
                    />
                  </Grid>
                </div>
              )
            }
          )}
        </div>
      </Grid>
    </section>
  )
}

const ArrowDown = (props) => (
  <svg
    width='104'
    height='136'
    viewBox='0 0 104 136'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    {...props}
  >
    <g clipPath='url(#clip0_201_9779)'>
      <path
        d='M99.7305 18.55C84.9814 20.3756 69.9107 23.613 56.899 31.0449C43.9345 38.4498 35.0439 50.2335 28.2185 63.3383C21.2588 76.7011 15.8633 90.9923 11.7544 105.464M21.8083 105.787C19.1932 108.652 16.5151 111.612 13.4727 114.036C12.711 114.643 11.1063 115.549 10.5133 116.296C9.23643 117.905 2.84936 109.406 1.41869 108.536'
        stroke='#7F6AFF'
        strokeWidth='2'
        strokeLinecap='round'
      />
    </g>
    <defs>
      <clipPath id='clip0_201_9779'>
        <rect width='104' height='136' fill='white' />
      </clipPath>
    </defs>
  </svg>
)

export default FeaturesSlider
